import React from 'react'
import { Select, Button, Popconfirm, message } from 'antd'
import { useSelect } from '@refinedev/antd'
import { useUpdate, useInvalidate } from '@refinedev/core'
import { SectionCard } from '../../theme/listkit'

interface MemberOption {
  user_id: string
  name: string | null
  role: string | null
}

/**
 * PromoteAdmin — grant a signed-up member admin access from the Accounts & Admins
 * page. A type-ahead searches every signed-up user (profiles with a non-null
 * user_id, i.e. an auth account) by name; promoting sets profiles.role='admin'
 * for that user. The admin login gate checks role==='admin', so the promoted
 * member can then sign in to this panel with their EXISTING app credentials.
 *
 * The write goes through the `profiles` resource — the `profiles: admin update`
 * RLS policy already lets an admin set another user's role, so no Edge Function
 * is needed. The search filters out anyone who is already an admin.
 */
export function PromoteAdmin() {
  const [userId, setUserId] = React.useState<string | undefined>(undefined)
  const [busy, setBusy] = React.useState(false)
  const { mutate: update } = useUpdate()
  const invalidate = useInvalidate()

  // Signed-up, not-yet-admin members, searchable by name (server-side).
  const { selectProps } = useSelect<MemberOption>({
    resource: 'profiles',
    optionLabel: 'name',
    optionValue: 'user_id',
    onSearch: (text) => [{ field: 'name', operator: 'contains', value: text }],
    pagination: { pageSize: 20 },
    filters: [
      { field: 'user_id', operator: 'nnull', value: null },
      { field: 'role', operator: 'ne', value: 'admin' },
    ],
  })

  function promote() {
    if (!userId) return
    setBusy(true)
    update(
      {
        resource: 'profiles',
        id: userId,
        meta: { idColumnName: 'user_id' },
        values: { role: 'admin' },
        successNotification: false,
        errorNotification: false,
        mutationMode: 'pessimistic',
      },
      {
        onSuccess: () => {
          message.success(
            'Member promoted to admin — they can now sign in to the admin panel with their app login.',
          )
          setUserId(undefined)
          invalidate({ resource: 'admin_users', invalidates: ['list'] })
        },
        onError: (e: unknown) =>
          message.error(`Could not promote: ${e instanceof Error ? e.message : 'unknown error'}`),
        onSettled: () => setBusy(false),
      },
    )
  }

  return (
    <SectionCard
      title="Promote a member to admin"
      sub="Search a signed-up member by name and grant them admin access. They sign in to this panel with their existing app login."
      style={{ marginBottom: 16 }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Select
          {...(selectProps as object)}
          showSearch
          allowClear
          style={{ flex: 1, minWidth: 280 }}
          placeholder="Search members by name…"
          value={userId}
          onChange={(v) => setUserId((v as string) || undefined)}
          filterOption={false}
          data-testid="promote-admin-select"
        />
        <Popconfirm
          title="Make this member an admin?"
          description="They will get full access to the admin panel."
          okText="Yes, make admin"
          cancelText="Cancel"
          onConfirm={promote}
          disabled={!userId}
        >
          <Button type="primary" disabled={!userId} loading={busy} data-testid="promote-admin-button">
            Make Admin
          </Button>
        </Popconfirm>
      </div>
    </SectionCard>
  )
}
